import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { isChangeManager } from './changePermissions';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { changesApi, type ChangeCatalogKind } from '../../services/changesApi';
import type {
  ApprovalOrderMode,
  ChangeCatalogKindItem,
} from '../../types/changes';
import { getApiErrorMessage } from '../../utils/apiError';
import { DEFAULT_APPROVAL_SETTINGS, type ApprovalSettings } from './changeApprovalPlan';
import {
  CHANGE_CATALOG_REGISTRY,
  catalogHubPath,
  groupCatalogItemsForNav,
  resolveCatalogNavGroupId,
  resolveCatalogTab,
} from './catalogRegistry';
import ChangeCatalogManager from './components/ChangeCatalogManager';
import ChangeCommitteesPanel from './components/ChangeCommitteesPanel';
import ChangeCreateFormBuilder from './components/ChangeCreateFormBuilder';
import ChangeDetailSummaryConfigurator from './components/ChangeDetailSummaryConfigurator';
import ChangeApprovalSettingsPanel from './components/ChangeApprovalSettingsPanel';
import ChangeApprovalFlowHubPage from './ChangeApprovalFlowHubPage';
import ChangeSapRequestTransportPanel from './components/ChangeSapRequestTransportPanel';
import ChangeFormFieldBuilder from './components/ChangeFormFieldBuilder';
import ChangePostImplFormBuilder from './components/ChangePostImplFormBuilder';
import ChangeDetailTabsHubPage from './ChangeDetailTabsHubPage';
import ChangeSubtaskFormBuilder from './components/ChangeSubtaskFormBuilder';
import ChangeCabSlidesPanel from './components/ChangeCabSlidesPanel';
import ChangeTypePoliciesPanel from './components/ChangeTypePoliciesPanel';
import ChangeWorkGroupsPanel from './components/ChangeWorkGroupsPanel';
import ChangeInternalSubtaskTemplatesPanel from './components/ChangeInternalSubtaskTemplatesPanel';
import './ChangeCatalogsHubPage.css';

const ChangeEmailTriggersPanel = lazy(() => import('./components/ChangeEmailTriggersPanel'));
const ChangeEmailQueuedPanel = lazy(() => import('./components/ChangeEmailQueuedPanel'));

const CreatorApprovalCatalog: React.FC = () => {
  const { user } = useAuth();
  const canManage = isChangeManager(user);
  const [settings, setSettings] = useState<ApprovalSettings>(DEFAULT_APPROVAL_SETTINGS);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const orderMode: ApprovalOrderMode = settings.approval_order_mode || 'livre';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await changesApi.getApprovalSettings();
      setSettings(next);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar o formulário do aprovador.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await changesApi.replaceApprovalSettings({
        enable_riscos_impactos: settings.enable_riscos_impactos,
        enable_camud_go_no_go: settings.enable_camud_go_no_go,
        enable_gestao_mudancas: settings.enable_gestao_mudancas !== false,
        creator_approver_roles: settings.creator_approver_roles || ['criador', 'gestor'],
        plan_editor_roles: settings.plan_editor_roles || ['admin', 'gestor', 'criador'],
        approval_order_mode: settings.approval_order_mode || 'livre',
        form_editable_statuses:
          settings.form_editable_statuses?.length
            ? settings.form_editable_statuses
            : ['avaliar', 'agendado'],
      });
      setSettings(saved);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a aprovação do criador.'));
    } finally {
      setSaving(false);
    }
  };

  const builderApi = useMemo(
    () => ({
      load: changesApi.getApprovalFormSchema,
      createField: changesApi.createApprovalFormField,
      updateField: changesApi.updateApprovalFormField,
      deleteField: changesApi.deleteApprovalFormField,
      reorderFields: changesApi.reorderApprovalFormFields,
    }),
    [],
  );

  if (loading) {
    return <p className="text-muted mb-0">Carregando formulário do aprovador...</p>;
  }

  return (
    <div>
      <Alert variant="light" className="border mb-3">
        <strong>O que o aprovador vê:</strong> ao Aprovar ou Reprovar (área, gestor de mudanças ou
        emergencial), aparecem a justificativa obrigatória e os campos que você montar abaixo —
        texto, número, lista, Sim/Não, moeda (R$/€/US$) etc.
      </Alert>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card className="mb-4 border">
        <Card.Header className="fw-semibold">1. Ordem das aprovações (mudança normal)</Card.Header>
        <Card.Body>
          <p className="text-muted small">
            O card <strong>Criador / gestor</strong> foi desativado. Em Autorizar ficam o{' '}
            <strong>Gestor de mudanças</strong> (quando ligado no plano) e as áreas. A ordem abaixo
            define se as áreas esperam o gestor do módulo.
          </p>
          <h6 className="mb-2">Ordem das aprovações</h6>
          <Form.Check
            type="radio"
            id="approval-form-order-livre"
            name="approval-form-order"
            className="mb-2"
            label="Livre — todos votam juntos"
            checked={orderMode === 'livre'}
            disabled={!canManage}
            onChange={() => setSettings((prev) => ({ ...prev, approval_order_mode: 'livre' }))}
          />
          <Form.Check
            type="radio"
            id="approval-form-order-criador"
            name="approval-form-order"
            className="mb-3"
            label="Gestor de mudanças primeiro — as áreas só votam depois deste card aprovado"
            checked={orderMode === 'criador_primeiro'}
            disabled={!canManage}
            onChange={() =>
              setSettings((prev) => ({ ...prev, approval_order_mode: 'criador_primeiro' }))
            }
          />
          {canManage ? (
            <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar ordem'}
            </Button>
          ) : (
            <p className="text-muted small mb-0">Somente administrador ou analista altera este cadastro.</p>
          )}
        </Card.Body>
      </Card>

      <h5 className="mb-2">2. Campos que todo aprovador preenche</h5>
      <ChangeFormFieldBuilder
        heading="Perguntas e campos da decisão"
        description="Estes campos aparecem para qualquer aprovador na vez dele (áreas, gestores emergenciais e criador/gestor). Inclui texto, número, decimal e moeda com R$, € ou US$."
        api={builderApi}
      />
    </div>
  );
};

function renderCatalogPanel(item: ChangeCatalogKindItem) {
  if (item.kind === 'create_form') {
    return <ChangeCreateFormBuilder />;
  }
  if (item.kind === 'detail_summary') {
    return <ChangeDetailSummaryConfigurator embedded />;
  }
  if (item.kind === 'subtask_form') {
    return <ChangeSubtaskFormBuilder />;
  }
  if (item.kind === 'post_impl_form') {
    return <ChangePostImplFormBuilder />;
  }
  if (item.kind === 'detail_tabs') {
    return <ChangeDetailTabsHubPage />;
  }
  if (item.kind === 'simple') {
    return (
      <ChangeCatalogManager
        kind={item.slug as ChangeCatalogKind}
        title={item.title}
        itemLabel={item.label}
        description={item.description}
        embedded
      />
    );
  }
  if (item.kind === 'change_types') {
    return <ChangeTypePoliciesPanel />;
  }
  if (item.kind === 'emergency_policy') {
    return <ChangeApprovalFlowHubPage />;
  }
  if (item.kind === 'sap_request_transport') {
    return <ChangeSapRequestTransportPanel />;
  }
  if (item.kind === 'approval_settings') {
    return <ChangeApprovalSettingsPanel />;
  }
  if (item.kind === 'approval_form') {
    return <CreatorApprovalCatalog />;
  }
  if (item.kind === 'change_email_triggers') {
    return (
      <Suspense fallback={<p className="text-muted small py-3">Carregando configuração de e-mail…</p>}>
        <ChangeEmailTriggersPanel />
      </Suspense>
    );
  }
  if (item.kind === 'change_email_queued') {
    return (
      <Suspense fallback={<p className="text-muted small py-3">Carregando e-mails agendados…</p>}>
        <ChangeEmailQueuedPanel />
      </Suspense>
    );
  }
  if (item.kind === 'agenda_email') {
    return <ChangeCommitteesPanel initialSection="email" layoutOnly />;
  }
  if (item.kind === 'committees') {
    return <ChangeCommitteesPanel />;
  }
  if (item.kind === 'cab_slides') {
    return <ChangeCabSlidesPanel />;
  }
  if (item.kind === 'internal_subtask_templates' || item.kind === 'internal_auto_subtasks') {
    return <ChangeInternalSubtaskTemplatesPanel />;
  }
  if (item.kind === 'work_groups') {
    return <ChangeWorkGroupsPanel />;
  }
  return <ChangeWorkGroupsPanel />;
}

const ChangeCatalogsHubPage: React.FC = () => {
  const { user } = useAuth();
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<ChangeCatalogKindItem[]>(CHANGE_CATALOG_REGISTRY);
  const [error, setError] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<string[]>(() => [
    resolveCatalogNavGroupId(tab),
  ]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const canManageHub = isChangeManager(user);

  useEffect(() => {
    if (!canManageHub) return;
    let cancelled = false;
    void changesApi
      .listCatalogRegistry()
      .then((data) => {
        if (cancelled || !data.items?.length) return;
        setItems(data.items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(getApiErrorMessage(err, 'Não foi possível atualizar a lista de cadastros.'));
      });
    return () => {
      cancelled = true;
    };
  }, [canManageHub]);

  const activeTab = resolveCatalogTab(tab, items);
  const navGroups = useMemo(() => groupCatalogItemsForNav(items), [items]);
  const activeGroupId = resolveCatalogNavGroupId(activeTab, navGroups);
  const activeItem = items.find((item) => item.tab === activeTab) || null;
  const previousActiveGroupId = useRef(activeGroupId);

  useEffect(() => {
    // Só abre o grupo ao trocar de tema (ex.: Estrutura → Comunicação).
    // Não impede o usuário de fechar o grupo da aba atual.
    if (previousActiveGroupId.current === activeGroupId) return;
    previousActiveGroupId.current = activeGroupId;
    setOpenGroups((prev) => (prev.includes(activeGroupId) ? prev : [...prev, activeGroupId]));
  }, [activeGroupId]);

  if (!canManageHub) {
    return <Navigate to="/changes" replace />;
  }

  if (!tab || tab !== activeTab) {
    return <Navigate to={catalogHubPath(activeTab)} replace />;
  }

  return (
    <div className="container-fluid py-3 change-catalogs-hub">
      <h2 className="mb-1">Cadastros de mudança</h2>
      <p className="text-muted mb-3 change-catalogs-hub__intro">
        Cadastros agrupados por tema: Estrutura, Aprovações, Formulários e Comunicação. Escolha o
        item no menu à esquerda.
      </p>
      {error ? (
        <Alert variant="warning" dismissible onClose={() => setError(null)}>
          {error} Exibindo a lista local.
        </Alert>
      ) : null}
      <Row className="g-3 g-lg-4">
        {!isSidebarCollapsed ? (
          <Col md={4} lg={3}>
            <nav aria-label="Cadastros de mudança" className="change-catalogs-nav">
              <div className="change-catalogs-nav__toolbar">
                <span className="change-catalogs-nav__toolbar-title">Menu</span>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setIsSidebarCollapsed(true)}
                >
                  Minimizar
                </Button>
              </div>
              <Accordion
                alwaysOpen
                activeKey={openGroups}
                onSelect={(eventKey) => {
                  const keys = Array.isArray(eventKey)
                    ? eventKey.map(String)
                    : eventKey
                      ? [String(eventKey)]
                      : [];
                  setOpenGroups(keys);
                }}
              >
                {navGroups.map((group) => (
                  <Accordion.Item eventKey={group.id} key={group.id}>
                    <Accordion.Header>{group.label}</Accordion.Header>
                    <Accordion.Body>
                      <div className="change-catalogs-nav__links">
                        {group.items.map((item) => {
                          const active = activeTab === item.tab;
                          return (
                            <button
                              key={item.slug}
                              type="button"
                              className={`change-catalogs-nav__link${active ? ' is-active' : ''}`}
                              aria-current={active ? 'page' : undefined}
                              onClick={() => navigate(catalogHubPath(item.tab))}
                            >
                              {item.title}
                            </button>
                          );
                        })}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </nav>
          </Col>
        ) : null}
        <Col md={isSidebarCollapsed ? 12 : 8} lg={isSidebarCollapsed ? 12 : 9}>
          {isSidebarCollapsed ? (
            <div className="d-flex justify-content-end mb-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setIsSidebarCollapsed(false)}
              >
                Mostrar menu lateral
              </Button>
            </div>
          ) : null}
          <div className="change-catalogs-hub__panel" key={activeTab}>
            {activeItem ? renderCatalogPanel(activeItem) : null}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ChangeCatalogsHubPage;
